import React, { useMemo } from "react";
import {
  useGetAgentStatusQuery,
  useGetPresenceQuery,
  type PresenceUser,
} from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import { usePresenceHeartbeat } from "./usePresenceHeartbeat";

interface PresenceBarProps {
  chatType: string;
  /** whether the group chat is actually being viewed — gates the heartbeat */
  active?: boolean;
}

/** sitebot identity — rendered alongside humans when the agent is "in" the chat */
const SITEBOT_USERNAME = "sitebot";
const SITEBOT_EMOJI = "🧠";

/**
 * The agent reports `presence` on its status as the contact/channel name it's currently
 * engaged with (see n450s_backend cycle.ts: `event.contact || event.channel`). Group chat
 * is the "general" channel; personal chat is the normalised viewer username. We surface
 * the bot in the presence list whenever its presence matches this view.
 */
function botIsInChat(
  agentPresence: string | null | undefined,
  chatType: string,
  viewerUsername: string | undefined,
): boolean {
  if (!agentPresence) return false;
  if (chatType === "group") return agentPresence === "general";
  if (chatType === "personal") {
    if (!viewerUsername) return false;
    const normalised = viewerUsername.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    return agentPresence === normalised;
  }
  return false;
}

export function PresenceBar({ chatType, active = true }: PresenceBarProps) {
  const user = useAppSelector((s) => s.auth);

  // SSE handles real-time updates; polling is a 60s fallback
  const { data } = useGetPresenceQuery(undefined, {
    pollingInterval: 60_000,
  });

  // bot presence comes from the agent status SSE — no extra round-trip needed
  const { data: agentStatus } = useGetAgentStatusQuery();

  // only register presence while this chat view is actually open + visible
  usePresenceHeartbeat(chatType, active);

  const viewers = useMemo<PresenceUser[]>(() => {
    // preserve original behaviour: show every human currently registered as present,
    // regardless of chatType. Each user only has one map entry server-side so they don't
    // double-up across chats.
    const humans: PresenceUser[] = [...(data?.users || [])];
    if (botIsInChat(agentStatus?.presence, chatType, user.username || undefined)) {
      // append the bot as a synthetic viewer so it renders inline with humans
      humans.push({
        username: SITEBOT_USERNAME,
        chatType,
        lastSeen: new Date().toISOString(),
        userEmoji: SITEBOT_EMOJI,
      });
    }
    return humans;
  }, [data?.users, agentStatus?.presence, chatType, user.username]);

  if (viewers.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 border-b border-gray-200">
        no one here right now
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 overflow-x-auto">
      <span className="text-xs text-gray-400 shrink-0">in chat:</span>
      <div className="flex items-center gap-1.5">
        {viewers.map((u) => {
          const isSelf = u.username === user.username;
          // fall back to a neutral glyph if the user has no profile emoji yet, so the row
          // never collapses to an empty slot.
          const glyph = u.userEmoji && u.userEmoji.length > 0 ? u.userEmoji : "👤";
          return (
            <span
              key={u.username}
              title={u.username}
              aria-label={u.username}
              className={`inline-flex items-center justify-center text-base leading-none ${
                isSelf ? "opacity-100" : "opacity-90"
              }`}
            >
              {glyph}
            </span>
          );
        })}
      </div>
    </div>
  );
}
